import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Building2, RefreshCw, Search, X, Phone, Mail, MapPin, Monitor,
  Printer, Loader2, Ban, LifeBuoy, Plus, FileText,
} from 'lucide-react';
import { supportAPI } from '../../services/supportApi';
import {
  CopyButton, TicketFormModal, TypeBadge, PriorityBadge, StatusBadge, dtfmt,
} from './supportCommon';
import CashierDevices from './CashierDevices';
import PrinterTimings from '../printers/PrinterTimings';
import SupportNewStore, { CreatedStore } from './SupportNewStore';
import SupportSetup, { SetupItem, setupMissing } from './SupportSetup';
import SupportPlatforms from './SupportPlatforms';
import SupportBilling from './SupportBilling';
import i18n from '../../i18n';
import { cancellationPrintWarning } from '../../utils/cancellationPrint';

/**
 * Support «Καταστήματα»: ό,τι χρειάζεται όταν χτυπάει το τηλέφωνο —
 * επικοινωνία, AnyDesk με copy, agent ανά υπολογιστή (έκδοση), πρόσφατες παραγγελίες
 * (με ακύρωση κατόπιν αιτήματος), παραστατικά που κόλλησαν («Επανάληψη»), επιδόσεις
 * εκτυπωτών, πρόσβαση (κωδικός καταστήματος / PIN) και tickets του καταστήματος.
 */

export interface SupportStore {
  id: number;
  name: string;
  display_name?: string | null;
  is_active: boolean;
  phone_number?: string | null;
  email?: string | null;
  address?: string | null;
  city?: string | null;
  postal_code?: string | null;
  anydesk_id?: string | null;
  anydesk_password?: string | null;
  delivery?: boolean; takeaway?: boolean; dine_in?: boolean; kiosk_enabled?: boolean;
  warehouse?: boolean; barcode_enabled?: boolean;
  analytics_tier?: string | null; caller_id_provider?: string | null;
  platform_efood?: boolean; platform_wolt?: boolean; platform_box?: boolean;
  organization_id?: number | null;
  organization_name?: string | null;
  last_order_at?: string | null;
  last_doc_at?: string | null;
  open_tickets: number;
  /** Παραστατικά που δεν έχουν φτάσει στον πάροχο, παλαιότερα από 5′ (25/10/2026). */
  fiscal_pending?: number;
}

const ORDER_STATUS_LABELS: Record<string, string> = {
  pending: 'Σε αναμονή', accepted: 'Αποδεκτή', preparing: 'Ετοιμάζεται',
  ready: 'Έτοιμη', dispatched: 'Σε διανομή', delivered: 'Παραδόθηκε',
  completed: 'Ολοκληρώθηκε', cancelled: 'Ακυρωμένη', rejected: 'Απορρίφθηκε',
};
const orderStatusLabel = (s: string) => ORDER_STATUS_LABELS[s] || s;

const OrderStatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const cls = status === 'cancelled' || status === 'rejected' ? 'bg-red-500/15 text-red-700 dark:text-red-300'
    : status === 'completed' || status === 'delivered' ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
    : status === 'dispatched' ? 'bg-sky-500/15 text-sky-700 dark:text-sky-300'
    : 'bg-amber-500/15 text-amber-800 dark:text-amber-300';
  return <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${cls}`}>{orderStatusLabel(status)}</span>;
};

const featureChips = (s: SupportStore): string[] => {
  const chips: string[] = [];
  if (s.delivery) chips.push('Delivery');
  if (s.takeaway) chips.push('Take away');
  if (s.dine_in) chips.push('Dine-in');
  if (s.kiosk_enabled) chips.push('Kiosk');
  if (s.warehouse) chips.push('Αποθήκη');
  if (s.barcode_enabled) chips.push('Barcode');
  if (s.platform_efood) chips.push('efood');
  if (s.platform_wolt) chips.push('Wolt');
  if (s.platform_box) chips.push('Box');
  if (s.caller_id_provider && s.caller_id_provider !== 'none') chips.push('Caller ID');
  if (s.analytics_tier === 'premium') chips.push('Premium Analytics');
  return chips;
};

/** «Άνοιγμα» από την «Προσοχή»: αυτό το κατάστημα, σε αυτή την καρτέλα (nonce = κάθε πάτημα). */
export interface StoreFocusRequest { storeId: number; tab: string; nonce: number }

const SupportStoresTab: React.FC<{ focus?: StoreFocusRequest | null; onOpenBilling?: (storeId: number) => void }> = ({
  focus = null, onOpenBilling,
}) => {
  const [stores, setStores] = useState<SupportStore[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sel, setSel] = useState<SupportStore | null>(null);
  // Μόλις δημιουργημένο: η κάρτα ανοίγει στο «Στήσιμο» με το τι έγινε.
  const [selOpen, setSelOpen] = useState<{ tab: CardTab; msg: string | null }>({ tab: 'overview', msg: null });
  const handledFocus = useRef<number | null>(null);
  const [showNew, setShowNew] = useState(false);

  const load = async (): Promise<SupportStore[]> => {
    try {
      setLoading(true);
      const d: any = await supportAPI.getStores();
      const list = d.stores || [];
      setStores(list);
      // Αν είναι ανοιχτή καρτέλα καταστήματος, δείξ' τη φρέσκια γραμμή (π.χ.
      // ενημερωμένος αριθμός ανοιχτών tickets) χωρίς να κλείσει το modal.
      setSel(prev => (prev ? (list.find((s: SupportStore) => s.id === prev.id) ?? prev) : prev));
      return list;
    } catch (e) {
      console.error('Support stores failed:', e);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const onCreated = async (c: CreatedStore) => {
    setShowNew(false);
    const list = await load();
    const cat = c.catalog;
    const catalogMsg = !cat ? ''
      : cat.pulled ? (cat.errors && cat.errors.length
        ? ` Κατάλογος: ${cat.applied || 0} εφαρμογές, ${cat.errors.length} με σφάλμα.` : ' Ο κατάλογος της αλυσίδας μπήκε.')
        : cat.reason === 'error' ? ` Ο κατάλογος ΔΕΝ μπήκε (${cat.error}) — θα μπει στο πρώτο Ζ.` : '';
    setSelOpen({ tab: 'setup', msg: `Το κατάστημα #${c.id} «${c.name}» δημιουργήθηκε.${catalogMsg}` });
    setSel(list.find(s => s.id === c.id) || { id: c.id, name: c.name, is_active: true, open_tickets: 0, organization_id: c.organization_id });
  };
  const openStore = (s: SupportStore) => { setSelOpen({ tab: 'overview', msg: null }); setSel(s); };
  // Μία φορά ανά «Άνοιγμα» — και όταν η λίστα φορτώσει μετά το πάτημα.
  useEffect(() => {
    if (!focus || handledFocus.current === focus.nonce) return;
    const s = stores.find(x => x.id === focus.storeId);
    if (!s) return;
    handledFocus.current = focus.nonce;
    setSelOpen({ tab: focus.tab as CardTab, msg: null });
    setSel(s);
  }, [focus, stores]);
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return stores;
    return stores.filter(s =>
      s.name.toLowerCase().includes(q)
      || (s.display_name || '').toLowerCase().includes(q)
      || (s.organization_name || '').toLowerCase().includes(q)
      || (s.phone_number || '').includes(q)
      || (s.city || '').toLowerCase().includes(q)
      || String(s.id) === q
    );
  }, [stores, search]);

  // Full-page spinner μόνο στο πρώτο φόρτωμα — τα refresh (π.χ. μετά από ticket)
  // δεν πρέπει να ξεμοντάρουν τη λίστα ή την ανοιχτή καρτέλα.
  if (loading && stores.length === 0) {
    return <div className="flex items-center justify-center py-32"><RefreshCw className="h-8 w-8 animate-spin text-cyan-600 dark:text-cyan-400" /></div>;
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-t1 flex items-center gap-2">
            <Building2 className="h-6 w-6 text-cyan-600 dark:text-cyan-400" />Καταστήματα
          </h1>
          <p className="text-sm text-t3">Στοιχεία επικοινωνίας, AnyDesk, παραγγελίες και tickets ανά κατάστημα</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-0 sm:flex-none">
            <Search className="h-4 w-4 text-t4 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Αναζήτηση: όνομα, οργανισμός, τηλέφωνο, πόλη, #id"
              className="w-full sm:w-72 bg-surface-2 dark:bg-surface-1 border border-line text-t1 rounded-lg pl-9 pr-3 py-2 text-sm placeholder:text-t4"
            />
          </div>
          <button onClick={() => load()} className="p-2 text-t3 hover:text-t1 bg-surface-2 dark:bg-surface-1 border border-line rounded-lg" title="Ανανέωση">
            <RefreshCw className="h-4 w-4" />
          </button>
          <button type="button" onClick={() => setShowNew(true)} data-testid="support-new-store-open"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-cyan-600 text-white hover:bg-cyan-700">
            <Plus className="h-4 w-4" />Νέο κατάστημα
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {filtered.map(s => (
          <button
            key={s.id} onClick={() => openStore(s)}
            className={`text-left bg-surface-2 dark:bg-surface-1 border border-line hover:border-cyan-500/50 rounded-2xl p-4 transition-colors ${!s.is_active ? 'opacity-50' : ''}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-t1 truncate">
                  {s.display_name || s.name}
                  {s.organization_name && <span className="text-t3 font-normal"> ({s.organization_name})</span>}
                </p>
                <p className="text-xs text-t4">#{s.id} · {s.name}</p>
              </div>
              <span className="shrink-0 flex items-center gap-1">
                {(s.fiscal_pending || 0) > 0 && (
                  <span title={`${s.fiscal_pending} παραστατικά περιμένουν τον πάροχο`} data-testid={`store-fiscal-pending-${s.id}`}
                    className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-red-500/15 text-red-700 dark:text-red-300">
                    <FileText className="h-3 w-3" />{s.fiscal_pending}
                  </span>
                )}
                {s.open_tickets > 0 && (
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-800 dark:text-amber-300">
                    <LifeBuoy className="h-3 w-3" />{s.open_tickets}
                  </span>
                )}
              </span>
            </div>
            <div className="mt-2 space-y-1 text-xs text-t3">
              {s.phone_number && <p className="flex items-center gap-1.5"><Phone className="h-3 w-3" />{s.phone_number}</p>}
              {(s.city || s.address) && <p className="flex items-center gap-1.5 truncate"><MapPin className="h-3 w-3 shrink-0" />{[s.address, s.city].filter(Boolean).join(', ')}</p>}
              {s.anydesk_id && <p className="flex items-center gap-1.5"><Monitor className="h-3 w-3" />AnyDesk {s.anydesk_id}</p>}
            </div>
            <div className="mt-2 flex flex-wrap gap-1">
              {featureChips(s).map(c => (
                <span key={c} className="text-[10px] px-1.5 py-0.5 rounded-full bg-surface-2 dark:bg-surface-2/70 text-t2">{c}</span>
              ))}
            </div>
          </button>
        ))}
        {filtered.length === 0 && (
          <p className="text-sm text-t4 col-span-full text-center py-12">Κανένα κατάστημα δεν ταιριάζει.</p>
        )}
      </div>

      {sel && (
        <StoreSupportModal key={sel.id} store={sel} stores={stores} onClose={() => setSel(null)} onChanged={() => { load(); }}
          initialTab={selOpen.tab} justCreated={selOpen.msg} onOpenBilling={onOpenBilling} />
      )}
      <SupportNewStore open={showNew} onClose={() => setShowNew(false)} onCreated={onCreated} />
    </div>
  );
};

// ── Κάρτα καταστήματος για support ───────────────────────────────────────────
// Από 25/10/2026 σε καρτέλες: ό,τι χρειάζεται ο τεχνικός για να κρατήσει το κατάστημα ζωντανό — επισκόπηση
// (επικοινωνία, AnyDesk, agent ανά υπολογιστή), παραγγελίες (ακύρωση), παραστατικά που κόλλησαν («Επανάληψη»),
// επιδόσεις εκτυπωτών, πρόσβαση (κωδικός καταστήματος / PIN υπαλλήλων) και tickets.

export type CardTab = 'overview' | 'setup' | 'orders' | 'fiscal' | 'printers' | 'platforms' | 'billing' | 'access' | 'tickets';

interface AgentPc { version: string | null; machine: string | null; connected_since: string | null; printers: string[] }
interface AgentInfo { pcs: AgentPc[]; latest_version: string | null; target_version: string | null }
interface FiscalDoc {
  id: number; status: string; series: string | null; document_number: string | number | null;
  total_gross_amount: string | number | null; error_message: string | null; retry_count: number | null;
  created_at: string; order_short_code: string | null; document_type_name: string | null; invoice_type_code: string | null;
}
interface AccessEmployee {
  id: number; first_name: string | null; last_name: string | null; email: string | null; is_active: boolean;
  pin_length: number | null; last_login: string | null; role_name: string | null; can_reset_pin: boolean;
}
interface AccessInfo { username: string | null; can_reset_password: boolean; employees: AccessEmployee[] }

const FISCAL_STATUS_LABELS: Record<string, string> = {
  pending: 'Σε αναμονή', sending: 'Σε αποστολή', transmission_failure: 'Απώλεια διασύνδεσης',
  error: 'Σφάλμα', validation_error: 'Λάθος στοιχεία', unauthorized: 'Λάθος κλειδιά Oxygen',
};
const ROLE_LABELS: Record<string, string> = {
  store_admin: 'Διαχειριστής', manager: 'Υπεύθυνος', cashier: 'Ταμίας', waiter: 'Σερβιτόρος',
  driver: 'Διανομέας', kitchen: 'Κουζίνα', support: 'Support', super_admin: 'Super admin',
};
const errText = (e: any, fallback: string) => e?.response?.data?.error || e?.response?.data?.message || fallback;

const versionNewer = (a: string | null, b: string | null): boolean => {
  if (!a || !b) return false;
  const x = a.split('.').map(n => parseInt(n, 10) || 0);
  const y = b.split('.').map(n => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(x.length, y.length); i += 1) {
    if ((x[i] || 0) !== (y[i] || 0)) return (x[i] || 0) > (y[i] || 0);
  }
  return false;
};

const StoreSupportModal: React.FC<{
  store: SupportStore;
  stores: SupportStore[];
  onClose: () => void;
  onChanged: () => void;
  /** Μόλις δημιουργήθηκε από το «Νέο κατάστημα»: ανοίγει στο «Στήσιμο» με το μήνυμα της δημιουργίας. */
  initialTab?: CardTab;
  justCreated?: string | null;
  /** «Χρεώσεις» → «Άνοιγμα στις Χρεώσεις»: η σελίδα του πάνελ με αυτό το κατάστημα ανοιχτό. */
  onOpenBilling?: (storeId: number) => void;
}> = ({ store, stores, onClose, onChanged, initialTab = 'overview', justCreated = null, onOpenBilling }) => {
  const [tab, setTab] = useState<CardTab>(initialTab);
  const [orders, setOrders] = useState<any[]>([]);
  const [tickets, setTickets] = useState<any[]>([]);
  const [agent, setAgent] = useState<{ connected: boolean; connected_count: number; agent_printers: number } | null>(null);
  const [agentInfo, setAgentInfo] = useState<AgentInfo | null>(null);
  const [fiscal, setFiscal] = useState<FiscalDoc[] | null>(null);
  const [access, setAccess] = useState<AccessInfo | null>(null);
  const [setup, setSetup] = useState<SetupItem[] | null>(null);
  const [setupBusy, setSetupBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showNewTicket, setShowNewTicket] = useState(false);
  // «Συσκευές ταμείου» (οθόνη πελάτη + modem ανά PC) — δικό του παράθυρο πάνω από αυτό.
  const [showDevices, setShowDevices] = useState(false);
  // Ακύρωση: ποια παραγγελία έχει ανοιχτή φόρμα αιτιολογίας
  const [cancelFor, setCancelFor] = useState<any | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelFiscal, setCancelFiscal] = useState(true);
  // Παραστατικά: ποιο ξαναστέλνεται και τι απάντησε ο πάροχος.
  const [retrying, setRetrying] = useState<number | null>(null);
  const [retryNote, setRetryNote] = useState<Record<number, { ok: boolean; text: string }>>({});
  const [fiscalMsg, setFiscalMsg] = useState<string | null>(null);
  // Πρόσβαση: νέος κωδικός καταστήματος / νέο PIN ανά υπάλληλο.
  const [newPassword, setNewPassword] = useState('');
  const [pinFor, setPinFor] = useState<number | null>(null);
  const [newPin, setNewPin] = useState('');
  const [accessNote, setAccessNote] = useState<{ ok: boolean; text: string } | null>(null);

  const loadFiscal = async () => {
    try { const d: any = await supportAPI.getFiscalPending(store.id); setFiscal(d?.documents || []); } catch { setFiscal([]); }
  };
  const loadSetup = async () => {
    setSetupBusy(true);
    try { const d: any = await supportAPI.getSetup(store.id); setSetup(d?.items || null); } catch { setSetup(null); } finally { setSetupBusy(false); }
  };
  const loadAccess = async () => {
    try { setAccess(await supportAPI.getAccess(store.id) as any); } catch { setAccess(null); }
  };

  const load = async () => {
    try {
      setLoading(true);
      const [o, t, a, i] = await Promise.all([
        supportAPI.getStoreOrders(store.id) as any,
        supportAPI.getTickets({ store_id: store.id }) as any,
        supportAPI.getAgentStatus(store.id).catch(() => null) as any,
        supportAPI.getAgent(store.id).catch(() => null) as any,
      ]);
      setOrders(o?.orders || []);
      setTickets(t?.tickets || []);
      setAgent(a);
      setAgentInfo(i);
      loadFiscal();
      loadSetup();
    } catch (e) {
      console.error('Store support detail failed:', e);
    } finally {
      setLoading(false);
    }
  };
  // Μία φόρτωση ανά κατάστημα· η «Πρόσβαση» όταν ανοίξει η καρτέλα της.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [store.id]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (tab === 'access' && !access) loadAccess(); }, [tab]);

  const doCancel = async (order: any) => {
    const reason = cancelReason.trim();
    if (!reason) { alert('Γράψε αιτιολογία ακύρωσης.'); return; }
    try {
      setBusy(true);
      try {
        await supportAPI.cancelOrder(store.id, order.id, { reason });
      } catch (e: any) {
        if (e.response?.status === 409 && e.response?.data?.error === 'fiscal_document_issued') {
          const canFiscal = e.response.data.fiscal_can_cancel && cancelFiscal;
          const msg = canFiscal
            ? 'Έχει εκδοθεί παραστατικό. Θα ακυρωθεί η παραγγελία ΚΑΙ θα εκδοθεί ακυρωτικό στην ΑΑΔΕ. Συνέχεια;'
            : 'Έχει εκδοθεί παραστατικό — θα παραμείνει ενεργό στην ΑΑΔΕ (μόνο η παραγγελία ακυρώνεται). Συνέχεια;';
          if (!window.confirm(msg)) return;
          const r: any = await supportAPI.cancelOrder(store.id, order.id, { reason, force: true, cancelFiscal: canFiscal });
          if (r?.fiscal_cancel && !r.fiscal_cancel.done) {
            alert(`Η παραγγελία ακυρώθηκε, αλλά το ακυρωτικό ΔΕΝ εκδόθηκε: ${r.fiscal_cancel.error || 'άγνωστο σφάλμα'}`);
          }
          const printWarning = r?.fiscal_cancel?.done === true ? cancellationPrintWarning(r.fiscal_cancel, (k) => i18n.t(k)) : null;
          if (printWarning) alert(printWarning);
        } else if (e.response?.status === 409 && e.response?.data?.error === 'already_cancelled') {
          alert('Η παραγγελία είναι ήδη ακυρωμένη.');
        } else {
          throw e;
        }
      }
      setCancelFor(null);
      setCancelReason('');
      await load();
      onChanged();
    } catch (e: any) {
      alert(e.response?.data?.message || e.response?.data?.error || 'Αποτυχία ακύρωσης');
    } finally {
      setBusy(false);
    }
  };

  const doRetry = async (doc: FiscalDoc) => {
    setRetrying(doc.id);
    try {
      const r: any = await supportAPI.retryFiscalDocument(store.id, doc.id);
      const ok = r?.status === 'success';
      const label = `${doc.invoice_type_code || ''} ${doc.series ? `${doc.series}-` : ''}${doc.document_number ?? `#${doc.id}`}`.trim();
      // Το επιτυχημένο φεύγει από τη λίστα: το μήνυμα μένει πάνω από αυτήν.
      if (ok) setFiscalMsg(`Το ${label} στάλθηκε${r?.mark ? ` — MARK ${r.mark}` : ''}.`);
      else setRetryNote(prev => ({ ...prev, [doc.id]: { ok: false, text: r?.error_message || `Ακόμη «${FISCAL_STATUS_LABELS[r?.status] || r?.status || 'σε αναμονή'}».` } }));
      await loadFiscal();
      onChanged();
    } catch (e: any) {
      setRetryNote(prev => ({ ...prev, [doc.id]: { ok: false, text: errText(e, 'Η επανάληψη απέτυχε.') } }));
    } finally {
      setRetrying(null);
    }
  };

  const doResetPassword = async () => {
    if (newPassword.length < 6) { setAccessNote({ ok: false, text: 'Ο κωδικός θέλει τουλάχιστον 6 χαρακτήρες.' }); return; }
    if (!window.confirm(`Νέος κωδικός σύνδεσης για το «${store.display_name || store.name}». Ο παλιός δεν θα δουλεύει πια. Συνέχεια;`)) return;
    setBusy(true);
    try {
      await supportAPI.resetStorePassword(store.id, newPassword);
      setAccessNote({ ok: true, text: 'Ο κωδικός του καταστήματος άλλαξε. Πες τον στο κατάστημα — δεν ξαναφαίνεται.' });
      setNewPassword('');
    } catch (e: any) {
      setAccessNote({ ok: false, text: errText(e, 'Ο κωδικός δεν άλλαξε.') });
    } finally {
      setBusy(false);
    }
  };

  const doResetPin = async (emp: AccessEmployee) => {
    if (!/^\d{4,6}$/.test(newPin)) { setAccessNote({ ok: false, text: 'Το PIN θέλει 4 έως 6 ψηφία.' }); return; }
    setBusy(true);
    try {
      await supportAPI.resetEmployeePin(store.id, emp.id, newPin);
      setAccessNote({ ok: true, text: `Νέο PIN ${newPin.length} ψηφίων για ${[emp.first_name, emp.last_name].filter(Boolean).join(' ')}.` });
      setPinFor(null);
      setNewPin('');
      await loadAccess();
    } catch (e: any) {
      setAccessNote({ ok: false, text: errText(e, 'Το PIN δεν άλλαξε.') });
    } finally {
      setBusy(false);
    }
  };

  const eur = (v: any) => `${(parseFloat(v) || 0).toFixed(2)} €`;
  const fiscalCount = fiscal ? fiscal.length : 0;
  const tabs: Array<{ id: CardTab; label: string; badge?: number }> = [
    { id: 'overview', label: 'Επισκόπηση' },
    { id: 'setup', label: 'Στήσιμο', badge: setupMissing(setup) },
    { id: 'orders', label: 'Παραγγελίες' },
    { id: 'fiscal', label: 'Παραστατικά', badge: fiscalCount },
    { id: 'printers', label: 'Εκτυπωτές' },
    { id: 'platforms', label: 'Πλατφόρμες' },
    { id: 'billing', label: 'Χρεώσεις' },
    { id: 'access', label: 'Πρόσβαση' },
    { id: 'tickets', label: 'Tickets', badge: tickets.filter(t => t.status !== 'resolved').length },
  ];

  const overview = (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="bg-surface-2 dark:bg-surface-2/40 border border-line-strong rounded-xl p-4 space-y-2 text-sm">
          <p className="text-xs font-semibold text-t3 uppercase tracking-wide">Επικοινωνία</p>
          {store.phone_number ? (
            <p className="flex items-center gap-2 text-t2">
              <Phone className="h-4 w-4 text-t3" />
              <a href={`tel:${store.phone_number}`} className="hover:text-cyan-700 dark:hover:text-cyan-300">{store.phone_number}</a>
              <CopyButton value={store.phone_number} />
            </p>
          ) : <p className="text-t4 text-xs">Χωρίς τηλέφωνο</p>}
          {store.email && (
            <p className="flex items-center gap-2 text-t2">
              <Mail className="h-4 w-4 text-t3" />{store.email}
            </p>
          )}
          {(store.address || store.city) && (
            <p className="flex items-center gap-2 text-t2">
              <MapPin className="h-4 w-4 text-t3 shrink-0" />
              {[store.address, store.city, store.postal_code].filter(Boolean).join(', ')}
            </p>
          )}
          <p className="flex items-center gap-2 text-t2 text-xs pt-1">
            <FileText className="h-3.5 w-3.5" />
            Τελευταία παραγγελία: {dtfmt(store.last_order_at)} · παραστατικό: {dtfmt(store.last_doc_at)}
          </p>
        </div>
        <div className="bg-surface-2 dark:bg-surface-2/40 border border-line-strong rounded-xl p-4 space-y-2 text-sm">
          <p className="text-xs font-semibold text-t3 uppercase tracking-wide">Απομακρυσμένη πρόσβαση</p>
          {store.anydesk_id ? (
            <>
              <p className="flex items-center gap-2 text-t2">
                <Monitor className="h-4 w-4 text-t3" />
                AnyDesk: <b className="text-t1">{store.anydesk_id}</b>
                <CopyButton value={store.anydesk_id} title="Αντιγραφή AnyDesk ID" />
              </p>
              {store.anydesk_password && (
                <p className="flex items-center gap-2 text-t2">
                  Κωδικός: <span className="font-mono text-t1">{store.anydesk_password}</span>
                  <CopyButton value={store.anydesk_password} title="Αντιγραφή κωδικού" />
                </p>
              )}
            </>
          ) : <p className="text-t4 text-xs">Δεν έχει καταχωρηθεί AnyDesk</p>}
        </div>
      </div>

      <div className="bg-surface-2 dark:bg-surface-2/40 border border-line-strong rounded-xl p-4 space-y-3 text-sm" data-testid="support-agent">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <p className="text-xs font-semibold text-t3 uppercase tracking-wide flex items-center gap-1.5">
            <Printer className="h-3.5 w-3.5" />Print agent
          </p>
          <p className="text-xs text-t2">
            {agent === null ? <span className="text-t4">άγνωστο</span>
              : agent.connected
                ? <span className="text-emerald-600 dark:text-emerald-400 font-medium">συνδεδεμένος</span>
                : agent.agent_printers > 0
                  ? <span className="text-red-600 dark:text-red-400 font-medium">ΕΚΤΟΣ σύνδεσης</span>
                  : <span className="text-t4">δεν χρησιμοποιείται</span>}
            {agentInfo?.latest_version && <span className="text-t3"> · τελευταία έκδοση {agentInfo.latest_version}</span>}
            {agentInfo?.target_version && <span className="text-t3"> · στόχος {agentInfo.target_version}</span>}
          </p>
        </div>
        {agentInfo && agentInfo.pcs.length > 0 && (
          <div className="divide-y divide-line/60 border border-line rounded-lg overflow-hidden">
            {agentInfo.pcs.map((pc, i) => {
              const old = versionNewer(agentInfo.latest_version, pc.version);
              return (
                <div key={`${pc.machine || 'pc'}-${i}`} className="flex items-center justify-between gap-3 px-3 py-2" data-testid="support-agent-pc">
                  <div className="min-w-0">
                    <p className="text-t1 font-medium truncate">{pc.machine || `Υπολογιστής ${i + 1}`}</p>
                    <p className="text-xs text-t3 truncate">{pc.printers.join(', ') || 'χωρίς εκτυπωτή'} · από {dtfmt(pc.connected_since)}</p>
                  </div>
                  <span className={`shrink-0 text-xs font-semibold px-2 py-0.5 rounded-full ${old
                    ? 'bg-amber-500/15 text-amber-800 dark:text-amber-300' : 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'}`}
                    title={old ? `Υπάρχει νεότερη: ${agentInfo.latest_version}` : 'Τελευταία έκδοση'}>
                    {pc.version ? `v${pc.version}` : 'παλιός (v1)'}
                  </span>
                </div>
              );
            })}
          </div>
        )}
        {agent?.connected && (
          <button
            type="button"
            onClick={() => setShowDevices(true)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium bg-cyan-600/20 text-cyan-700 dark:text-cyan-300 hover:bg-cyan-600/30 rounded-lg"
            data-testid="support-open-devices"
          >
            <Monitor className="h-3.5 w-3.5" /> Συσκευές ταμείου (οθόνη πελάτη, αναγνώριση κλήσης)
          </button>
        )}
      </div>
    </div>
  );

  const ordersPane = (
    <div>
      <h4 className="text-sm font-semibold text-t1 mb-2">Πρόσφατες παραγγελίες (7 ημέρες)</h4>
      {orders.length > 0 ? (
        <div className="divide-y divide-line/60 border border-line rounded-lg overflow-hidden">
          {orders.map(o => (
            <div key={o.id} className="px-3 py-2 text-sm">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-t2">
                    <b>#{o.short_code || o.id}</b>
                    <span className="text-t3"> · {dtfmt(o.order_timestamp)}</span>
                    {o.customer_name && <span className="text-t3"> · {o.customer_name}</span>}
                  </p>
                  {o.status === 'cancelled' && o.cancel_reason && (
                    <p className="text-xs text-red-700/80 dark:text-red-300/80 truncate">{o.cancel_reason}</p>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <b className="text-t1">{eur(o.total_amount)}</b>
                  <OrderStatusBadge status={o.status} />
                  {o.fiscal_document_id && (
                    <span title="Έχει εκδοθεί παραστατικό" className="text-[10px] px-1.5 py-0.5 rounded-full bg-surface-3 dark:bg-surface-3/60 text-t2">ΑΠΥ</span>
                  )}
                  {o.status !== 'cancelled' && o.status !== 'rejected' && (
                    <button
                      onClick={() => { setCancelFor(cancelFor?.id === o.id ? null : o); setCancelReason(''); setCancelFiscal(true); }}
                      className="p-1.5 text-t4 hover:text-red-600 dark:hover:text-red-400 rounded"
                      title="Ακύρωση παραγγελίας"
                    >
                      <Ban className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
              {cancelFor?.id === o.id && (
                <div className="mt-2 bg-red-500/5 border border-red-500/30 rounded-lg p-3 space-y-2">
                  <input
                    value={cancelReason} onChange={(e) => setCancelReason(e.target.value)}
                    placeholder="Αιτιολογία ακύρωσης (υποχρεωτικό) — π.χ. αίτημα καταστήματος, τηλ. Χ"
                    className="w-full bg-surface-2 border border-line-strong text-t1 rounded-lg px-3 py-2 text-sm placeholder:text-t4"
                    autoFocus
                  />
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    {o.fiscal_document_id ? (
                      <label className="flex items-center gap-1.5 text-xs text-t2 cursor-pointer">
                        <input type="checkbox" checked={cancelFiscal} onChange={(e) => setCancelFiscal(e.target.checked)} className="accent-red-500" />
                        Έκδοση ακυρωτικού παραστατικού (ΑΑΔΕ)
                      </label>
                    ) : <span />}
                    <div className="flex items-center gap-2">
                      <button onClick={() => setCancelFor(null)} className="px-3 py-1.5 text-xs text-t3 hover:text-t1">Άκυρο</button>
                      <button
                        onClick={() => doCancel(o)} disabled={busy || !cancelReason.trim()}
                        className="px-3 py-1.5 bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white rounded-lg text-xs font-medium flex items-center gap-1.5"
                      >
                        {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                        Ακύρωση παραγγελίας
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-t4">Καμία παραγγελία τις τελευταίες 7 ημέρες.</p>
      )}
    </div>
  );

  const fiscalPane = (
    <div className="space-y-3" data-testid="support-fiscal">
      <p className="text-xs text-t3">
        Παραστατικά που δεν έχουν φτάσει στον πάροχο (Oxygen). Ο server τα ξαναστέλνει μόνος του· «Επανάληψη» = τώρα.
        Η ακύρωση παραστατικού δεν γίνεται από εδώ.
      </p>
      {fiscalMsg && <p role="status" className="text-sm text-emerald-700 dark:text-emerald-400">{fiscalMsg}</p>}
      {fiscal === null ? (
        <div className="py-8 grid place-items-center"><Loader2 className="h-5 w-5 animate-spin text-t3" /></div>
      ) : fiscal.length === 0 ? (
        <p className="text-sm text-emerald-700 dark:text-emerald-400">Όλα τα παραστατικά έχουν σταλεί.</p>
      ) : (
        <div className="divide-y divide-line/60 border border-line rounded-lg overflow-hidden">
          {fiscal.map(d => (
            <div key={d.id} className="px-3 py-2 text-sm" data-testid={`support-fiscal-${d.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-t2">
                    <b>{d.invoice_type_code || ''} {d.series ? `${d.series}-` : ''}{d.document_number ?? `#${d.id}`}</b>
                    <span className="text-t3"> · {dtfmt(d.created_at)}</span>
                    {d.order_short_code && <span className="text-t3"> · παραγγελία #{d.order_short_code}</span>}
                  </p>
                  {d.error_message && <p className="text-xs text-red-700/80 dark:text-red-300/80 break-words">{d.error_message}</p>}
                  <p className="text-xs text-t4">{d.document_type_name || ''}{d.retry_count ? ` · ${d.retry_count} προσπάθειες` : ''}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <b className="text-t1">{eur(d.total_gross_amount)}</b>
                  <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-800 dark:text-amber-300">
                    {FISCAL_STATUS_LABELS[d.status] || d.status}
                  </span>
                  <button
                    type="button" onClick={() => doRetry(d)} disabled={retrying !== null}
                    className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-cyan-600/20 text-cyan-700 dark:text-cyan-300 hover:bg-cyan-600/30 rounded-lg disabled:opacity-50"
                  >
                    {retrying === d.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                    Επανάληψη
                  </button>
                </div>
              </div>
              {retryNote[d.id] && (
                <p role="status" className={`mt-1 text-xs ${retryNote[d.id].ok ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                  {retryNote[d.id].text}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const accessPane = (
    <div className="space-y-4" data-testid="support-access">
      {accessNote && (
        <p role="status" className={`text-sm ${accessNote.ok ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>{accessNote.text}</p>
      )}
      {access === null ? (
        <div className="py-8 grid place-items-center"><Loader2 className="h-5 w-5 animate-spin text-t3" /></div>
      ) : (
        <>
          <div className="bg-surface-2 dark:bg-surface-2/40 border border-line-strong rounded-xl p-4 space-y-3 text-sm">
            <p className="text-xs font-semibold text-t3 uppercase tracking-wide">Σύνδεση καταστήματος</p>
            <p className="flex items-center gap-2 text-t2">
              Όνομα σύνδεσης: <b className="font-mono text-t1">{access.username || '—'}</b>
              {access.username && <CopyButton value={access.username} title="Αντιγραφή ονόματος σύνδεσης" />}
            </p>
            {access.can_reset_password ? (
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="text" value={newPassword} onChange={e => setNewPassword(e.target.value)} autoComplete="off"
                  aria-label="Νέος κωδικός καταστήματος" placeholder="Νέος κωδικός (τουλάχιστον 6)"
                  className="bg-surface-2 border border-line-strong text-t1 rounded-lg px-3 py-2 text-sm placeholder:text-t4 w-56"
                />
                <button type="button" onClick={doResetPassword} disabled={busy || newPassword.length < 6}
                  className="px-3 py-2 text-xs font-medium bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg disabled:opacity-50">
                  Αλλαγή κωδικού
                </button>
              </div>
            ) : <p className="text-xs text-t4">Ο κωδικός του κεντρικού καταστήματος δεν αλλάζει από το support.</p>}
          </div>

          <div>
            <h4 className="text-sm font-semibold text-t1 mb-2">Υπάλληλοι</h4>
            {access.employees.length === 0 ? <p className="text-xs text-t4">Κανένας υπάλληλος.</p> : (
              <div className="divide-y divide-line/60 border border-line rounded-lg overflow-hidden">
                {access.employees.map(emp => (
                  <div key={emp.id} className="px-3 py-2 text-sm" data-testid={`support-employee-${emp.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className={`truncate ${emp.is_active ? 'text-t1' : 'text-t4 line-through'}`}>
                          {[emp.first_name, emp.last_name].filter(Boolean).join(' ') || `#${emp.id}`}
                          <span className="text-t3"> · {ROLE_LABELS[emp.role_name || ''] || emp.role_name || '—'}</span>
                        </p>
                        <p className="text-xs text-t4">
                          PIN {emp.pin_length || 4} ψηφίων{emp.last_login ? ` · τελευταία σύνδεση ${dtfmt(emp.last_login)}` : ''}
                        </p>
                      </div>
                      {emp.can_reset_pin && pinFor !== emp.id && (
                        <button type="button" onClick={() => { setPinFor(emp.id); setNewPin(''); setAccessNote(null); }}
                          className="shrink-0 px-2.5 py-1.5 text-xs font-medium bg-cyan-600/20 text-cyan-700 dark:text-cyan-300 hover:bg-cyan-600/30 rounded-lg">
                          Νέο PIN
                        </button>
                      )}
                    </div>
                    {pinFor === emp.id && (
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <input
                          type="text" inputMode="numeric" value={newPin} maxLength={6} autoFocus autoComplete="off"
                          onChange={e => setNewPin(e.target.value.replace(/\D/g, ''))}
                          aria-label={`Νέο PIN για ${emp.first_name || ''}`} placeholder="4–6 ψηφία"
                          className="bg-surface-2 border border-line-strong text-t1 rounded-lg px-3 py-2 text-sm font-mono w-32"
                        />
                        <button type="button" onClick={() => doResetPin(emp)} disabled={busy || !/^\d{4,6}$/.test(newPin)}
                          className="px-3 py-2 text-xs font-medium bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg disabled:opacity-50">
                          Αποθήκευση PIN
                        </button>
                        <button type="button" onClick={() => setPinFor(null)} className="px-3 py-2 text-xs text-t3 hover:text-t1">Άκυρο</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );

  const ticketsPane = (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold text-t1 flex items-center gap-2">
          <LifeBuoy className="h-4 w-4 text-t3" />Tickets
        </h4>
        <button
          onClick={() => setShowNewTicket(true)}
          className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-cyan-600/20 text-cyan-700 dark:text-cyan-300 hover:bg-cyan-600/30 rounded-lg"
        >
          <Plus className="h-3.5 w-3.5" /> Νέο ticket
        </button>
      </div>
      {tickets.length > 0 ? (
        <div className="divide-y divide-line/60 border border-line rounded-lg overflow-hidden">
          {tickets.slice(0, 8).map(t => (
            <div key={t.id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
              <div className="min-w-0">
                <p className="text-t2 truncate">{t.title}</p>
                <p className="text-xs text-t4">{dtfmt(t.created_at)}{t.opened_by_name ? ` · ${t.opened_by_name}` : ''}</p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <TypeBadge type={t.type} />
                <PriorityBadge priority={t.priority} />
                <StatusBadge status={t.status} />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-t4">Κανένα ticket για αυτό το κατάστημα.</p>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      {/* Σταθερό ύψος: το παράθυρο δεν «πηδάει» όταν αλλάζει καρτέλα. */}
      <div className="bg-surface-2 dark:bg-surface-1 rounded-2xl border border-line w-full max-w-3xl shadow-2xl h-[min(92vh,760px)] flex flex-col"
        role="dialog" aria-modal="true" aria-label={store.display_name || store.name}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-line shrink-0">
          <div>
            <h3 className="text-lg font-bold text-t1">
              {store.display_name || store.name}
              {store.organization_name && <span className="text-t3 font-normal"> ({store.organization_name})</span>}
            </h3>
            <p className="text-xs text-t3">#{store.id} · {store.name}{!store.is_active && ' · ΑΝΕΝΕΡΓΟ'}</p>
          </div>
          <button onClick={onClose} className="p-1 text-t3 hover:text-t1" aria-label="Κλείσιμο"><X className="h-5 w-5" /></button>
        </div>

        <div role="tablist" aria-label="Κάρτα καταστήματος"
          className="flex gap-1 px-4 pt-3 overflow-x-auto shrink-0 border-b border-line [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {tabs.map(t => (
            <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)}
              data-testid={`support-tab-${t.id}`}
              className={`shrink-0 flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.id
                ? 'border-cyan-600 text-cyan-700 dark:text-cyan-300' : 'border-transparent text-t3 hover:text-t1'}`}>
              {t.label}
              {!!t.badge && (
                <span className={`min-w-[18px] px-1 rounded-full text-[11px] font-bold ${t.id === 'fiscal' || t.id === 'setup'
                  ? 'bg-red-500/15 text-red-700 dark:text-red-300' : 'bg-cyan-500/15 text-cyan-700 dark:text-cyan-300'}`}>{t.badge}</span>
              )}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24"><RefreshCw className="h-6 w-6 animate-spin text-cyan-600 dark:text-cyan-400" /></div>
        ) : (
          <div className="p-6 overflow-y-auto" role="tabpanel">
            {tab === 'overview' && overview}
            {tab === 'setup' && <SupportSetup items={setup} onRefresh={loadSetup} refreshing={setupBusy} justCreated={justCreated} />}
            {tab === 'orders' && ordersPane}
            {tab === 'fiscal' && fiscalPane}
            {tab === 'printers' && (
              <div data-testid="support-printer-timings">
                <PrinterTimings storeId={store.id} token={localStorage.getItem('xentrix_token') || ''}
                  path={`/api/support/stores/${store.id}/printer-timings`} />
              </div>
            )}
            {tab === 'platforms' && <SupportPlatforms storeId={store.id} onChanged={() => loadSetup()} />}
            {tab === 'billing' && <SupportBilling storeId={store.id} onOpenBilling={onOpenBilling} />}
            {tab === 'access' && accessPane}
            {tab === 'tickets' && ticketsPane}
          </div>
        )}
      </div>

      {showNewTicket && (
        <TicketFormModal
          stores={stores}
          fixedStoreId={store.id}
          onClose={() => setShowNewTicket(false)}
          onCreated={() => { load(); onChanged(); }}
        />
      )}
      {showDevices && (
        <CashierDevices storeId={store.id} storeName={store.display_name || store.name} onClose={() => setShowDevices(false)} />
      )}
    </div>
  );
};

export default SupportStoresTab;
